import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications, NOTIFICATION_TYPES, NOTIFICATION_PRIORITY } from '../contexts/NotificationContext';
import api from '../services/api';
import { getShortRobotId } from '../utils/robotIdHelper';

/**
 * BatteryLowNotification Component
 * 
 * Checks battery levels (12V and 48V) for all robots in the fleet after login
 * Shows persistent popup notifications for batteries at or below 20%
 * Notifications can only be dismissed by user manually clicking the close button
 */
const BatteryLowNotification = () => {
  const { user, isAuthenticated } = useAuth();
  const { addNotification, notifications, fetchNotifications } = useNotifications();
  const [checkedBatteries, setCheckedBatteries] = useState(false);
  const checkedBatteriesRef = useRef(false);
  
  // Track last notification creation time per robot/battery type (30-minute cooldown)
  // Format: Map<"robotId_batteryType", timestamp>
  const lastNotificationTimeRef = useRef(new Map());
  const COOLDOWN_MS = 30 * 60 * 1000; // 30 minutes

  // Check battery levels function - can be called multiple times
  const checkBatteryLevels = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    
    try {
      console.log('[BatteryLowNotification] Checking battery levels for fleet...');
      
      // Fetch fleet overview with battery data
      const response = await api.get('/heartbeats/fleet/overview');
      
      if (!response.data.success || !response.data.data) {
        console.warn('[BatteryLowNotification] Failed to fetch fleet data');
        return;
      }

      const robots = response.data.data;
      
      // Query database directly for recent battery low notifications (within 30 minutes)
      // This ensures we check the actual database state, not just in-memory state
      let recentNotificationKeys = new Set();
      try {
        const recentNotificationsResponse = await api.get('/notifications', {
          params: {
            limit: 100,
            unreadOnly: false,
            type: 'BATTERY_LOW'
          }
        });
        
        if (recentNotificationsResponse.data.success && recentNotificationsResponse.data.data) {
          const thirtyMinutesAgo = Date.now() - COOLDOWN_MS;
          recentNotificationKeys = new Set(
            recentNotificationsResponse.data.data
              .filter(n => {
                if (!n.metadata || !n.metadata.robotId || !n.metadata.batteryType) return false;
                // Check if notification was created within the last 30 minutes
                const notificationTime = new Date(n.createdAt).getTime();
                return notificationTime >= thirtyMinutesAgo;
              })
              .map(n => `${n.metadata.robotId}_${n.metadata.batteryType}`)
          );
          console.log(`[BatteryLowNotification] Found ${recentNotificationKeys.size} recent battery notifications in database`);
        }
      } catch (error) {
        console.warn('[BatteryLowNotification] Failed to fetch recent notifications from database:', error);
        // Fall back to checking in-memory notifications
        recentNotificationKeys = new Set(
          notifications
            .filter(n => {
              if (n.type !== NOTIFICATION_TYPES.BATTERY_LOW || !n.metadata) return false;
              const notificationTime = new Date(n.timestamp).getTime();
              const timeSinceNotification = Date.now() - notificationTime;
              return timeSinceNotification < COOLDOWN_MS;
            })
            .map(n => {
              if (n.metadata && n.metadata.robotId && n.metadata.batteryType) {
                return `${n.metadata.robotId}_${n.metadata.batteryType}`;
              }
              return null;
            })
            .filter(Boolean)
        );
      }
      
      // Get existing UNREAD notification IDs and metadata keys to avoid duplicates
      const existingNotificationIds = new Set(
        notifications
          .filter(n => n.type === NOTIFICATION_TYPES.BATTERY_LOW && !n.isRead)
          .map(n => n.id)
      );
      
      // Also create a set of metadata keys to check for duplicates (only unread)
      const existingNotificationKeys = new Set(
        notifications
          .filter(n => n.type === NOTIFICATION_TYPES.BATTERY_LOW && !n.isRead && n.metadata)
          .map(n => {
            if (n.metadata && n.metadata.robotId && n.metadata.batteryType) {
              return `${n.metadata.robotId}_${n.metadata.batteryType}`;
            }
            return null;
          })
          .filter(Boolean)
      );
      
      console.log(`[BatteryLowNotification] Existing unread notifications: ${existingNotificationIds.size} by ID, ${existingNotificationKeys.size} by metadata`);
      console.log(`[BatteryLowNotification] Total notifications in context: ${notifications.length}`);
      
      let newNotificationCount = 0;
      
      console.log(`[BatteryLowNotification] Found ${robots.length} robots to check`);
      
      // Check each robot's batteries
      for (const robot of robots) {
        const robotId = robot.robotId;
        const robotName = robot.name || robotId;
        
        console.log(`[BatteryLowNotification] ========================================`);
        console.log(`[BatteryLowNotification] Checking robot ${robotName} (${robotId})...`);
        
        // Check both 12V and 48V batteries - fetch battery details for each robot
        try {
          const shortRobotId =
            getShortRobotId(robotId) ||
            (robotId.includes('-') ? robotId.split('-').pop() : robotId);
          console.log(`[BatteryLowNotification] Fetching battery details for robot ${shortRobotId} (full ID: ${robotId})...`);

          const batteryDetailsResponse = await api.get(`/robots/${shortRobotId}/battery-details`);
          const body = batteryDetailsResponse.data;
          const payload = body?.data;
          const normalized =
            payload && typeof payload === 'object'
              ? {
                  '12V':
                    payload['12V'] && payload['12V'] !== 'No data' ? payload['12V'] : null,
                  '48V':
                    payload['48V'] && payload['48V'] !== 'No data' ? payload['48V'] : null
                }
              : null;

          console.log(`[BatteryLowNotification] Battery details response for ${robotName}:`, {
            success: body?.success,
            hasPayload: !!payload
          });

          if (body?.success && normalized) {
            const batteries = normalized;
            
            // Helper function to check a battery and create notification if low
            const checkBattery = async (batteryType, batteryData, robotId, robotName) => {
              if (!batteryData || batteryData === null) {
                console.log(`[BatteryLowNotification] ⚠️ ${robotName} ${batteryType} battery data is null or missing`);
                return false;
              }
              
              // Parse level (State of Charge) as a number
              let batteryLevel = null;
              if (batteryData.level !== null && batteryData.level !== undefined) {
                batteryLevel = parseFloat(batteryData.level);
                if (isNaN(batteryLevel)) {
                  console.warn(`[BatteryLowNotification] ⚠️ ${robotName} ${batteryType} battery level is not a valid number: ${batteryData.level}`);
                  batteryLevel = null;
                }
              }
              
              // Parse voltage as a number
              let batteryVoltage = null;
              if (batteryData.voltage !== null && batteryData.voltage !== undefined) {
                batteryVoltage = parseFloat(batteryData.voltage);
                if (isNaN(batteryVoltage)) {
                  console.warn(`[BatteryLowNotification] ⚠️ ${robotName} ${batteryType} battery voltage is not a valid number: ${batteryData.voltage}`);
                  batteryVoltage = null;
                }
              }
              
              console.log(`[BatteryLowNotification] ${robotName} ${batteryType} battery - SoC: ${batteryLevel}%, Voltage: ${batteryVoltage}V`);
              
              // Check if EITHER condition is met:
              // 1. State of Charge <= 20%
              // 2. Voltage < 12V
              const isLowSoC = batteryLevel !== null && !isNaN(batteryLevel) && batteryLevel <= 20;
              const isLowVoltage = batteryVoltage !== null && !isNaN(batteryVoltage) && batteryVoltage < 12;
              
              if (isLowSoC || isLowVoltage) {
                const notificationId = `battery_low_${batteryType.toLowerCase()}_${robotId}`;
                const metadataKey = `${robotId}_${batteryType}`;
                
                // Check if UNREAD notification already exists (by ID or metadata key)
                const hasUnreadNotification = existingNotificationIds.has(notificationId) || existingNotificationKeys.has(metadataKey);
                
                // Check 30-minute cooldown: check both in-memory ref and recent notifications from database
                const lastNotificationTime = lastNotificationTimeRef.current.get(metadataKey) || 0;
                const timeSinceLastNotification = Date.now() - lastNotificationTime;
                const hasRecentNotification = recentNotificationKeys.has(metadataKey);
                
                // Only create notification if:
                // 1. No unread notification exists, AND
                // 2. No recent notification exists (within 30 minutes), AND
                // 3. In-memory cooldown has expired (or never set)
                const canCreateNotification = !hasUnreadNotification && 
                                             !hasRecentNotification && 
                                             (lastNotificationTime === 0 || timeSinceLastNotification >= COOLDOWN_MS);
                
                if (canCreateNotification) {
                  // Build warning reasons
                  const warnings = [];
                  if (isLowSoC) warnings.push(`SoC: ${batteryLevel.toFixed(1)}%`);
                  if (isLowVoltage) warnings.push(`Voltage: ${batteryVoltage.toFixed(1)}V`);
                  const warningReason = warnings.join(', ');
                  
                  console.log(`[BatteryLowNotification] ⚠️ ${batteryType} battery critical for ${robotName}: ${warningReason}`);
                  
                  // Check if there was a previous dismissed notification (for logging)
                  const previousReadNotification = notifications.find(n => 
                    n.type === NOTIFICATION_TYPES.BATTERY_LOW && 
                    n.isRead && 
                    n.metadata &&
                    n.metadata.robotId === robotId &&
                    n.metadata.batteryType === batteryType
                  );
                  
                  if (previousReadNotification) {
                    console.log(`[BatteryLowNotification] ℹ️ Previous notification was dismissed, creating new one for persistent low battery`);
                  }
                  
                  // Build notification message
                  let message = `${robotName}: ${batteryType} battery critical - `;
                  if (isLowSoC && isLowVoltage) {
                    message += `SoC: ${batteryLevel.toFixed(1)}%, Voltage: ${batteryVoltage.toFixed(1)}V`;
                  } else if (isLowSoC) {
                    message += `SoC: ${batteryLevel.toFixed(1)}%${batteryVoltage !== null ? ` (Voltage: ${batteryVoltage.toFixed(1)}V)` : ''}`;
                  } else {
                    message += `Voltage: ${batteryVoltage.toFixed(1)}V${batteryLevel !== null ? ` (SoC: ${batteryLevel.toFixed(1)}%)` : ''}`;
                  }
                  
                  await addNotification({
                    id: notificationId,
                    title: '⚠️ Critical Battery Warning',
                    message: message,
                    type: NOTIFICATION_TYPES.BATTERY_LOW,
                    priority: NOTIFICATION_PRIORITY.HIGH,
                    duration: 0, // Persistent - only dismissed by user
                    metadata: {
                      robotId: robotId,
                      robotName: robotName,
                      batteryType: batteryType,
                      batteryLevel: batteryLevel,
                      batteryVoltage: batteryVoltage,
                      isLowSoC: isLowSoC,
                      isLowVoltage: isLowVoltage
                    },
                    saveToDb: true
                  });
                  
                  existingNotificationIds.add(notificationId);
                  existingNotificationKeys.add(metadataKey);
                  
                  // Update last notification time for this robot/battery type
                  lastNotificationTimeRef.current.set(metadataKey, Date.now());
                  
                  console.log(`[BatteryLowNotification] ✅ Created ${batteryType} critical battery notification for ${robotName} (${warningReason})`);
                  return true;
                } else {
                  if (hasUnreadNotification) {
                    console.log(`[BatteryLowNotification] ⏭️ ${batteryType} notification already exists (unread) for ${robotName} - skipping to avoid duplicate`);
                  } else if (hasRecentNotification) {
                    // Find the most recent notification to calculate remaining time
                    const recentNotif = notifications.find(n => {
                      if (n.type !== NOTIFICATION_TYPES.BATTERY_LOW || !n.metadata) return false;
                      const notifKey = n.metadata.robotId && n.metadata.batteryType 
                        ? `${n.metadata.robotId}_${n.metadata.batteryType}` 
                        : null;
                      return notifKey === metadataKey;
                    });
                    if (recentNotif) {
                      const notificationTime = new Date(recentNotif.timestamp).getTime();
                      const timeSinceNotification = Date.now() - notificationTime;
                      const minutesRemaining = Math.ceil((COOLDOWN_MS - timeSinceNotification) / 60000);
                      console.log(`[BatteryLowNotification] ⏭️ ${batteryType} notification for ${robotName} is in cooldown (${minutesRemaining} min remaining from recent notification)`);
                    } else {
                      console.log(`[BatteryLowNotification] ⏭️ ${batteryType} notification for ${robotName} is in cooldown (recent notification exists)`);
                    }
                  } else if (lastNotificationTime > 0 && timeSinceLastNotification < COOLDOWN_MS) {
                    const minutesRemaining = Math.ceil((COOLDOWN_MS - timeSinceLastNotification) / 60000);
                    console.log(`[BatteryLowNotification] ⏭️ ${batteryType} notification for ${robotName} is in cooldown (${minutesRemaining} min remaining from memory)`);
                  }
                  return false;
                }
              } else {
                // Battery is OK - clear cooldown so we can notify again if it goes low
                const metadataKey = `${robotId}_${batteryType}`;
                if (lastNotificationTimeRef.current.has(metadataKey)) {
                  lastNotificationTimeRef.current.delete(metadataKey);
                  console.log(`[BatteryLowNotification] ✓ ${robotName} ${batteryType} battery recovered - cleared cooldown`);
                }
                console.log(`[BatteryLowNotification] ✓ ${robotName} ${batteryType} battery OK - SoC: ${batteryLevel !== null ? batteryLevel.toFixed(1) + '%' : 'N/A'}, Voltage: ${batteryVoltage !== null ? batteryVoltage.toFixed(1) + 'V' : 'N/A'}`);
                return false;
              }
            };
            
            // Check 12V battery
            console.log(`[BatteryLowNotification] ========== Checking 12V battery for ${robotName} ==========`);
            console.log(`[BatteryLowNotification] ${robotName} 12V battery data:`, JSON.stringify(batteries['12V'], null, 2));
            const created12V = await checkBattery('12V', batteries['12V'], robotId, robotName);
            if (created12V) {
              newNotificationCount++;
              console.log(`[BatteryLowNotification] ✅ Successfully created 12V notification for ${robotName}`);
            } else {
              console.log(`[BatteryLowNotification] ⏭️ No 12V notification created for ${robotName} (may already exist or battery OK)`);
            }
            
            // Check 48V battery
            console.log(`[BatteryLowNotification] ========== Checking 48V battery for ${robotName} ==========`);
            console.log(`[BatteryLowNotification] ${robotName} 48V battery data:`, JSON.stringify(batteries['48V'], null, 2));
            const created48V = await checkBattery('48V', batteries['48V'], robotId, robotName);
            if (created48V) {
              newNotificationCount++;
              console.log(`[BatteryLowNotification] ✅ Successfully created 48V notification for ${robotName}`);
            } else {
              console.log(`[BatteryLowNotification] ⏭️ No 48V notification created for ${robotName} (may already exist or battery OK)`);
            }
            
            console.log(`[BatteryLowNotification] ========================================`);
            
          } else {
            console.warn(`[BatteryLowNotification] ⚠️ No battery data in response for ${robotName}`, batteryDetailsResponse.data);
          }
        } catch (batteryError) {
          console.error(`[BatteryLowNotification] ❌ Failed to fetch battery details for ${robotId}:`, batteryError);
          // Continue checking other robots
        }
      }
      
      console.log(`[BatteryLowNotification] ✅ Battery check completed. Created ${newNotificationCount} new notification(s).`);
      
      // Refresh notifications from DB if we created any, to ensure they appear in the popup
      if (newNotificationCount > 0 && fetchNotifications) {
        console.log('[BatteryLowNotification] Refreshing notifications from database...');
        setTimeout(() => {
          fetchNotifications();
        }, 1000); // Wait 1 second for DB to save, then refresh
      }
      
    } catch (error) {
      console.error('[BatteryLowNotification] ❌ Error checking battery levels:', error);
    }
  }, [isAuthenticated, user, addNotification, notifications, fetchNotifications]);

  // Check battery levels after user is authenticated (initial check)
  useEffect(() => {
    if (!isAuthenticated || !user) {
      console.log('[BatteryLowNotification] User not authenticated, skipping battery check');
      return;
    }
    
    console.log('[BatteryLowNotification] Component mounted/updated, user authenticated:', user?.email);
    
    checkedBatteriesRef.current = false;
    setCheckedBatteries(false);

    console.log('[BatteryLowNotification] Running immediate battery check after login...');
    checkBatteryLevels();

    const delayed = setTimeout(() => {
      console.log('[BatteryLowNotification] Running delayed battery check (5s)...');
      checkBatteryLevels();
      checkedBatteriesRef.current = true;
      setCheckedBatteries(true);
    }, 5000);

    return () => clearTimeout(delayed);
  }, [isAuthenticated, user, checkBatteryLevels]);

  // Periodically check battery levels (every 1 minute for more frequent checks)
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Start periodic checks immediately, but first check happens after initial delay
    const periodicInterval = setInterval(() => {
      console.log('[BatteryLowNotification] Running periodic battery check...');
      checkBatteryLevels();
    }, 5 * 60 * 1000); // Every 5 min — lowers load on Timescale /api/robots/.../battery-details
    
    return () => clearInterval(periodicInterval);
  }, [isAuthenticated, user, checkBatteryLevels]);

  // Reset checked state when user changes
  useEffect(() => {
    checkedBatteriesRef.current = false;
    setCheckedBatteries(false);
  }, [user?.id]);

  // Expose checkBatteryLevels function globally for debugging (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.checkBatteryLevels = checkBatteryLevels;
      console.log('[BatteryLowNotification] ✅ Manual check function available: window.checkBatteryLevels()');
    }
    
    return () => {
      if (process.env.NODE_ENV === 'development') {
        delete window.checkBatteryLevels;
      }
    };
  }, [checkBatteryLevels]);

  // This component doesn't render anything - it just triggers notifications
  return null;
};

export default BatteryLowNotification;

